import { Suspense, useRef, useCallback, RefObject } from "react";
import { Canvas, ThreeEvent } from "@react-three/fiber";
import { Mesh, Vector2 } from "three";

import { StickerConfig } from "@/lib/sticker";
import { useInteractionMode } from "@/hooks/sticker/useInteractionMode";

import { Loader } from "../common/Loader";
import { ToolBar } from "./ToolBar";
import { StickerScene } from "./StickerScene";

interface StickerPreviewProps {
  selectedModel: string;
  stickerConfig: StickerConfig;
  stickerMode: ReturnType<
    typeof import("@/hooks/sticker/useStickerMode").useStickerMode
  >;
}

export const StickerPreview = ({
  selectedModel,
  stickerConfig,
  stickerMode,
}: StickerPreviewProps) => {
  const meshRef = useRef<Mesh>(null!);
  const {
    mode,
    textureData,
    decalData,
    switchMode,
    updateTextureData,
    updateDecalData,
  } = stickerMode;

  const { mode: interactionMode, switchMode: switchInteractionMode } =
    useInteractionMode();

  // 用于存储resetCamera函数引用
  const resetCameraRef = useRef<(() => void) | null>(null);

  const handleMeshClick = useCallback(
    (event: ThreeEvent<MouseEvent>) => {
      // 只在贴图交互模式下处理贴图逻辑
      if (interactionMode !== "sticker") return;
      if (!event.uv || !(event.object instanceof Mesh)) return;

      meshRef.current = event.object;

      if (mode === "texture") {
        updateTextureData(event.uv);
      } else {
        if (!event.face) return;
        updateDecalData(event.point, event.face.normal);
      }
    },
    [interactionMode, mode, updateTextureData, updateDecalData],
  );

  const handleTextureUvUpdate = useCallback(
    (uvPoint: Vector2) => {
      updateTextureData(uvPoint);
    },
    [updateTextureData],
  );

  const handleCameraReset = useCallback(() => {
    if (resetCameraRef.current) {
      resetCameraRef.current();
    }
  }, []);

  return (
    <div className="h-full bg-card flex items-center justify-center w-full overflow-hidden relative">
      <ToolBar
        interactionMode={interactionMode}
        stickerMode={mode}
        onInteractionModeChange={switchInteractionMode}
        onStickerModeChange={switchMode}
        onCameraReset={handleCameraReset}
      />

      <Suspense fallback={<Loader />}>
        <Canvas
          shadows
          dpr={[1, 2]}
          style={{ background: "#f9f9f9" }}
          camera={{ position: [0, 0, 8], fov: 45 }}
        >
          <StickerScene
            selectedModel={selectedModel}
            stickerConfig={stickerConfig}
            mode={mode}
            interactionMode={interactionMode}
            meshRef={meshRef}
            textureUvPoint={textureData.uvPoint}
            decalPoint={decalData.point}
            decalNormal={decalData.normal}
            onMeshClick={handleMeshClick}
            onTextureUvUpdate={handleTextureUvUpdate}
            onCameraReset={resetCameraRef}
          />
        </Canvas>
      </Suspense>
    </div>
  );
};
